/**
 * CallScriptPanel — call-script picker + reader for whatever chrome wraps it
 * (CallScriptModal popout, InCallModal right pane).
 *
 * Fetches admin-authored scripts (category='call_script') from the server,
 * already resolved against the currently-loaded contact and the signed-in
 * rep via `GET /console/call-scripts`. The server-side resolver is the
 * single source of truth — every token (`{{contact.first_name}}`,
 * `{{contact.cf.state}}`, `{{rep.name}}`, spintax `{a|b}`) renders the same
 * way here as in any other surface that uses the same endpoint.
 *
 * Picker-first UX (#821/#824): the panel never auto-displays a script.
 * It opens on a picker — search box, a "Recent" group (per rep, persisted
 * in localStorage so it survives reloads), and the full active list. The
 * rep clicks a script to read it; "← All scripts" returns to the picker.
 *
 * Mid-script field population (#512): when the `contactVersion` prop changes
 * (bumped by the Console after any contact edit), the panel re-fetches so
 * the open script's resolved content updates live during the call. The
 * rep's current selection is preserved across that re-fetch.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  Clock,
  Copy,
  Search,
  Type,
} from 'lucide-react';
import DOMPurify from 'dompurify';
import { apiGet } from '../../hooks/useApi';
import { useAuth } from '../../providers/AuthProvider';
import { tagsToArray } from '../../lib/template-tags';
import './RichTextEditor.css';

interface CallScriptPanelProps {
  contactId?: string | null;
  contactName: string;
  brand?: string;
  progressPct?: number;
  courseName?: string;
  repName?: string;
  /** Bump after any contact field edit to force a re-fetch of resolved
   *  content. Absent/unchanging means "no live updates needed". */
  contactVersion?: number;
}

interface FetchedScript {
  id: string;
  title: string;
  category: string | null;
  /** Serialized as a real array by the server; normalize defensively. */
  tags: string[] | string | null;
  body: string;
  warnings: string[];
}

interface ResponseShape {
  scripts: FetchedScript[];
  has_contact: boolean;
}

// ── Per-rep recents (persisted) ──────────────────────────────────────────

const RECENTS_KEY_PREFIX = 'apex.call-scripts.recents.';
const RECENTS_MAX = 5;

function recentsStorageKey(repKey: string): string {
  return `${RECENTS_KEY_PREFIX}${repKey}`;
}

function readRecents(repKey: string): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(recentsStorageKey(repKey));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter((v): v is string => typeof v === 'string');
  } catch {
    // corrupted entry — treat as empty
  }
  return [];
}

function writeRecents(repKey: string, ids: string[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      recentsStorageKey(repKey),
      JSON.stringify(ids.slice(0, RECENTS_MAX)),
    );
  } catch {
    // quota / privacy mode — recents just won't persist this session
  }
}

// ── Reader font size (persisted) ─────────────────────────────────────────
//
// A rep reads scripts aloud on a call, so the body needs to be larger than
// the old static 13px and adjustable on the fly (#1559). Rather than hardcode
// a size, we expose an S/M/L/XL stepper mirroring the app-wide FontSizeToggle
// scale. Sizes are in rem so they stack with the global text-size toggle and
// the whole render (body + headings, which are em-relative) scales as one.

const READER_SIZE_KEY = 'apex.call-scripts.reader-size';

type ReaderSize = 'sm' | 'md' | 'lg' | 'xl';

const READER_SIZES: ReaderSize[] = ['sm', 'md', 'lg', 'xl'];

const READER_SIZE_LABELS: Record<ReaderSize, string> = {
  sm: 'S',
  md: 'M',
  lg: 'L',
  xl: 'XL',
};

const READER_SIZE_REM: Record<ReaderSize, string> = {
  sm: '0.875rem', // 14px at default root
  md: '1rem', // 16px
  lg: '1.125rem', // 18px
  xl: '1.375rem', // 22px
};

// Default a step up so scripts open comfortably readable on a call; the rep
// nudges it from there and the choice persists per browser.
const READER_SIZE_DEFAULT: ReaderSize = 'lg';

const isReaderSize = (v: string | null): v is ReaderSize =>
  v === 'sm' || v === 'md' || v === 'lg' || v === 'xl';

function readReaderSize(): ReaderSize {
  if (typeof window === 'undefined') return READER_SIZE_DEFAULT;
  try {
    const raw = window.localStorage.getItem(READER_SIZE_KEY);
    if (isReaderSize(raw)) return raw;
  } catch {
    // corrupted / unavailable storage — fall back to the default
  }
  return READER_SIZE_DEFAULT;
}

function writeReaderSize(size: ReaderSize): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(READER_SIZE_KEY, size);
  } catch {
    // quota / privacy mode — size just won't persist this session
  }
}

// ── Component ────────────────────────────────────────────────────────────

export function CallScriptPanel(props: CallScriptPanelProps) {
  const { user } = useAuth();
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [scripts, setScripts] = useState<FetchedScript[]>([]);
  // null = picker view. Never auto-populated — the rep picks (#821/#824).
  const [activeId, setActiveId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Persisted per-browser script-reader text size (#1559).
  const [readerSize, setReaderSize] = useState<ReaderSize>(() => readReaderSize());
  const changeReaderSize = useCallback((size: ReaderSize) => {
    setReaderSize(size);
    writeReaderSize(size);
  }, []);

  const { contactId, contactVersion } = props;

  // Recents are keyed per rep so a shared workstation doesn't bleed one
  // rep's list into another's. `sub` is the stable Keycloak id.
  const repKey = user?.sub || user?.email || 'anon';
  const [recentIds, setRecentIds] = useState<string[]>(() => readRecents(repKey));
  useEffect(() => {
    setRecentIds(readRecents(repKey));
  }, [repKey]);

  const fetchScripts = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const qs = contactId ? `?contact_id=${encodeURIComponent(contactId)}` : '';
      // Console routes mount at /console/* (NOT /api/console/*) — see
      // src/app.ts. The earlier path was 404'ing for every rep on every
      // call, which is what surfaced as "Failed to load scripts."
      const data = await apiGet<ResponseShape>(`/console/call-scripts${qs}`);
      setScripts(data.scripts || []);
      // Preserve the rep's current selection across live re-fetches
      // (contact edits mid-call); if the script disappeared, fall back to
      // the picker. Never auto-select.
      setActiveId((prev) =>
        prev && (data.scripts || []).some((s) => s.id === prev) ? prev : null,
      );
    } catch {
      setError(true);
    }
    setLoading(false);
  }, [contactId]);

  // Re-fetch on mount, when the contact changes, and when `contactVersion`
  // bumps (caller signals a field edit).
  useEffect(() => {
    void fetchScripts();
  }, [fetchScripts, contactVersion]);

  const active = scripts.find((s) => s.id === activeId);

  const openScript = (id: string) => {
    setActiveId(id);
    setRecentIds((prev) => {
      const next = [id, ...prev.filter((x) => x !== id)].slice(0, RECENTS_MAX);
      writeRecents(repKey, next);
      return next;
    });
  };

  const copy = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text.replace(/\n• /g, '\n- '));
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch {
      // no-op — copy-to-clipboard may be blocked in some contexts
    }
  };

  return (
    <div className="space-y-3">
      {loading && (
        <div className="text-[0.75rem] text-muted-foreground">Loading scripts&hellip;</div>
      )}

      {!loading && error && (
        <div className="text-[0.75rem] text-destructive">Failed to load scripts.</div>
      )}

      {/* Reader view — the rep explicitly picked a script. */}
      {!loading && !error && active && (
        <div>
          <div className="mb-2 flex items-center justify-between gap-2">
            <button
              onClick={() => setActiveId(null)}
              className="text-[0.6875rem] font-semibold text-primary hover:text-primary/80 cursor-pointer flex items-center gap-1"
            >
              <ArrowLeft className="w-3 h-3 stroke-[2]" /> All scripts
            </button>
            <ReaderSizeControl size={readerSize} onChange={changeReaderSize} />
          </div>
          <ActiveScript
            active={active}
            copiedKey={copiedKey}
            copy={copy}
            fontSize={READER_SIZE_REM[readerSize]}
          />
        </div>
      )}

      {/* Picker view — search + per-rep recents + full list. No script is
          ever auto-displayed (#821/#824). */}
      {!loading && !error && !active && scripts.length > 0 && (
        <ScriptPicker
          scripts={scripts}
          recentIds={recentIds}
          query={query}
          setQuery={setQuery}
          onOpen={openScript}
        />
      )}

      {/* Empty state — no fallback content, just a clear note pointing
          admins at the place to author scripts. The on-call surface
          should never invent script copy on its own. */}
      {!loading && !error && scripts.length === 0 && (
        <div className="text-[0.75rem] text-muted-foreground italic px-3 py-4 rounded-lg bg-secondary/30 border border-border/40">
          No call scripts configured yet. Admins can author them in Admin Console → Call Scripts.
        </div>
      )}
    </div>
  );
}

// ── Picker subview ───────────────────────────────────────────────────────

function ScriptPicker({
  scripts,
  recentIds,
  query,
  setQuery,
  onOpen,
}: {
  scripts: FetchedScript[];
  recentIds: string[];
  query: string;
  setQuery: (q: string) => void;
  onOpen: (id: string) => void;
}) {
  const q = query.trim().toLowerCase();

  const matches = (s: FetchedScript): boolean => {
    if (!q) return true;
    return (
      (s.title || '').toLowerCase().includes(q) ||
      htmlToText(s.body).toLowerCase().includes(q) ||
      tagsToArray(s.tags).some((t) => t.toLowerCase().includes(q))
    );
  };

  const filtered = scripts.filter(matches);
  const recents = recentIds
    .map((id) => filtered.find((s) => s.id === id))
    .filter((s): s is FetchedScript => !!s);
  const recentSet = new Set(recents.map((s) => s.id));
  const rest = filtered.filter((s) => !recentSet.has(s.id));

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search scripts…"
          aria-label="Search scripts"
          className="w-full pl-8 pr-3 py-2 border border-border/60 rounded-lg text-[0.75rem] bg-card outline-none focus:border-primary/40"
        />
      </div>

      {filtered.length === 0 && (
        <div className="text-[0.75rem] text-muted-foreground italic px-1">
          No scripts match your search.
        </div>
      )}

      {recents.length > 0 && (
        <ScriptGroup
          label="Recent"
          icon={<Clock className="w-3 h-3 stroke-[2]" />}
          scripts={recents}
          onOpen={onOpen}
        />
      )}

      {rest.length > 0 && (
        <ScriptGroup
          label={recents.length > 0 ? 'All scripts' : 'Scripts'}
          scripts={rest}
          onOpen={onOpen}
        />
      )}
    </div>
  );
}

function ScriptGroup({
  label,
  icon,
  scripts,
  onOpen,
}: {
  label: string;
  icon?: ReactNode;
  scripts: FetchedScript[];
  onOpen: (id: string) => void;
}) {
  return (
    <div>
      <div className="flex items-center gap-1 mb-1.5 text-[0.625rem] font-bold uppercase tracking-wider text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="space-y-1.5">
        {scripts.map((s) => {
          const tags = tagsToArray(s.tags);
          return (
            <button
              key={s.id}
              onClick={() => onOpen(s.id)}
              className="w-full text-left px-3 py-2 rounded-lg border border-border/60 bg-card hover:border-primary/40 hover:bg-primary/5 transition-colors cursor-pointer group"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-[0.8125rem] font-semibold text-foreground truncate">
                  {s.title}
                </span>
                <ChevronRight className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary flex-shrink-0" />
              </div>
              <div className="text-[0.6875rem] text-muted-foreground truncate mt-0.5">
                {htmlToText(s.body)}
              </div>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {tags.slice(0, 4).map((tag) => (
                    <span
                      key={tag}
                      className="text-[0.5625rem] bg-secondary text-muted-foreground border border-border/60 px-1 py-0.5 rounded"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── ActiveScript subview ─────────────────────────────────────────────────

function ActiveScript({
  active,
  copiedKey,
  copy,
  fontSize,
}: {
  active: FetchedScript;
  copiedKey: string | null;
  copy: (key: string, text: string) => Promise<void>;
  /** rem font-size for the script body; drives the em-relative headings too. */
  fontSize: string;
}) {
  // Sanitize on every body change. Admin authors can technically introduce
  // raw HTML through the Tiptap editor; DOMPurify drops anything dangerous
  // before we render it. Plain-text legacy scripts pass through unchanged
  // (the `whitespace: pre-wrap` in `.rte-rendered` preserves their newlines).
  const sanitized = useMemo(() => DOMPurify.sanitize(active.body || ''), [active.body]);

  // The Copy button copies the textContent so reps can paste a clean
  // unformatted copy into chat / a different window.
  const plainBody = useMemo(() => {
    if (typeof window === 'undefined') return active.body;
    const el = document.createElement('div');
    el.innerHTML = sanitized;
    return el.textContent ?? '';
  }, [sanitized, active.body]);

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[0.625rem] font-bold uppercase tracking-wider text-muted-foreground">
          {active.title}
        </span>
        <button
          onClick={() => copy(active.id, plainBody)}
          className="text-[0.625rem] text-primary hover:text-primary/80 cursor-pointer flex items-center gap-1"
        >
          {copiedKey === active.id ? (
            <>
              <CheckCircle2 className="w-3 h-3 stroke-[2]" /> Copied
            </>
          ) : (
            <>
              <Copy className="w-3 h-3 stroke-[1.5]" /> Copy
            </>
          )}
        </button>
      </div>
      {/* sanitized via DOMPurify above. Font size is rep-controlled (#1559)
          and set inline so it overrides .rte-rendered's base and scales the
          em-relative headings with it. */}
      <div
        className="rte-rendered text-foreground leading-relaxed bg-card border border-border/60 rounded-lg p-3"
        style={{ fontSize }}
        dangerouslySetInnerHTML={{ __html: sanitized }}
      />

      {active.warnings.length > 0 && (
        <div className="mt-2 px-3 py-2 rounded-lg bg-amber-500/5 border border-amber-500/20 text-amber-700 text-[0.625rem] space-y-0.5">
          <div className="flex items-center gap-1.5 font-semibold uppercase tracking-wider">
            <AlertTriangle className="w-3 h-3 stroke-[2]" /> Unresolved tokens
          </div>
          <ul className="list-disc list-inside">
            {active.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Reader size control ──────────────────────────────────────────────────

/**
 * Compact S/M/L/XL stepper for the script reader. Mirrors the app-wide
 * FontSizeToggle scale but scoped to the on-call script body, so a rep can
 * size it for reading aloud without changing the rest of the UI (#1559).
 */
function ReaderSizeControl({
  size,
  onChange,
}: {
  size: ReaderSize;
  onChange: (size: ReaderSize) => void;
}) {
  return (
    <div
      className="flex items-center gap-0.5 rounded-lg border border-border/60 bg-secondary/30 p-0.5"
      role="group"
      aria-label="Script text size"
    >
      <Type className="w-3 h-3 stroke-[1.5] text-muted-foreground ml-1 mr-0.5" aria-hidden="true" />
      {READER_SIZES.map((s) => {
        const isActive = s === size;
        return (
          <button
            key={s}
            onClick={() => onChange(s)}
            aria-pressed={isActive}
            aria-label={`Text size ${READER_SIZE_LABELS[s]}`}
            title={`Text size ${READER_SIZE_LABELS[s]}`}
            className={`px-1.5 py-0.5 rounded text-[0.625rem] font-semibold leading-none cursor-pointer transition-colors ${
              isActive
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {READER_SIZE_LABELS[s]}
          </button>
        );
      })}
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────

/**
 * Flatten resolved script HTML to a one-line plain-text snippet for the
 * picker rows and search matching. Regex strip is fine here — bodies are
 * DOMPurify-sanitized before any HTML rendering, and the snippet itself is
 * rendered as text.
 */
function htmlToText(html: string): string {
  if (!html) return '';
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}
